import React, { useEffect, useRef, useState } from 'react';
import {
    Box,
    Grid,
    Card,
    CardContent,
    Typography,
    TextField,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Checkbox,
    FormControlLabel,
    ListItemText,
    Chip,
    OutlinedInput,
    Divider
} from '@mui/material';
import { LocalizationProvider, DesktopDatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import DiscountIcon from '@mui/icons-material/Discount';
import PlansIcon from '@mui/icons-material/ListAlt';
import { useLocation } from 'react-router-dom';
import authService from '../Auth/AuthService';

const discountTypes = ['FLAT', 'PERCENTAGE', 'REFERRAL'];

const CreateCouponForm = ({ onSuccess }) => {

    const [subscriptionPlans, setSubscriptionPlans] = useState([])
    const location = useLocation();
    const { plan } = location.state || {};
    const hasFetched = useRef(false);

    useEffect(() => {

        if (hasFetched.current) return;
        hasFetched.current = true;

        if (plan === undefined) {
            authService
                .restGet(authService.pathUrl("GET", "/subscription/plans"))
                .then((res) => {
                    setSubscriptionPlans(res || []);
                })
                .catch((error) => {
                    console.error("Failed to fetch subscription plans:", error.message);
                })
        } else
            setSubscriptionPlans([plan]);
    }, []);

    const [coupon, setCoupon] = useState({
        codename: '',
        discountType: 'FLAT',
        discountAmount: '',
        discountPercentage: '',
        startDate: new Date(),
        endDate: new Date(),
        applicablePlans: [],
        isActive: true,
        oneTimePerUser: false,
        autoApply: false,
        maxUsageCount: ''
    });

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setCoupon((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleDateChange = (field, date) => {
        setCoupon((prev) => ({
            ...prev,
            [field]: date
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (coupon.applicablePlans.length === 0 && coupon.discountType !== 'REFERRAL') {
            return;
        }
        const payload = {
            ...coupon,
            planIds: coupon.applicablePlans.map((id) => parseInt(id))
        };
        const url = authService.pathUrl("POST", '/coupons');
        const response = await authService.postAuth(url, payload);
        if (onSuccess) onSuccess();
        console.log('Submitted Coupon:', response);
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box sx={{ maxWidth: 1100, mx: 'auto', p: 0 }}>
                <Card elevation={6} sx={{ borderRadius: 4, p: 3 }}>
                    <CardContent>
                        <Typography variant="h5" fontWeight="bold" gutterBottom>
                            <DiscountIcon sx={{ mr: 1, verticalAlign: 'middle', color: '#1b1914' }} />
                            Create Coupon
                        </Typography>
                        <Divider sx={{ mb: 5 }} />
                        <Box component="form" onSubmit={handleSubmit} mt={3}>
                            <Grid container spacing={3}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        label="Coupon Code"
                                        name="codename"
                                        fullWidth
                                        required
                                        value={coupon.codename}
                                        onChange={handleChange}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth>
                                        <InputLabel>Discount Type</InputLabel>
                                        <Select
                                            name="discountType"
                                            value={coupon.discountType}
                                            onChange={handleChange}
                                            label="Discount Type"
                                        >
                                            {discountTypes.map((type) => (
                                                <MenuItem key={type} value={type}>
                                                    {type}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                {coupon.discountType === 'FLAT' ? (
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Discount Amount"
                                            name="discountAmount"
                                            type="number"
                                            fullWidth
                                            required
                                            value={coupon.discountAmount}
                                            onChange={handleChange}
                                        />
                                    </Grid>
                                ) : (
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Discount (%)"
                                            name="discountPercentage"
                                            type="number"
                                            fullWidth
                                            required
                                            value={coupon.discountPercentage}
                                            onChange={handleChange}
                                        />
                                    </Grid>
                                )}

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        label="Max Usage Count"
                                        name="maxUsageCount"
                                        type="number"
                                        fullWidth
                                        value={coupon.maxUsageCount}
                                        onChange={handleChange}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <DesktopDatePicker
                                        label="Start Date"
                                        value={coupon.startDate}
                                        onChange={(date) => handleDateChange('startDate', date)}
                                        renderInput={(params) => <TextField {...params} fullWidth />}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <DesktopDatePicker
                                        label="End Date"
                                        value={coupon.endDate}
                                        onChange={(date) => handleDateChange('endDate', date)}
                                        renderInput={(params) => <TextField {...params} fullWidth />}
                                    />
                                </Grid>

                                {coupon.discountType !== 'REFERRAL' && (
                                    <Grid item xs={12}>
                                        <FormControl fullWidth>
                                            <InputLabel>
                                                <PlansIcon />
                                                Applicable Plans
                                            </InputLabel>
                                            <Select
                                                multiple
                                                name="applicablePlans"
                                                value={coupon.applicablePlans}
                                                onChange={handleChange}
                                                required
                                                input={<OutlinedInput label="Applicable Plans" />}
                                                renderValue={(selected) => (
                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                        {selected.map((id) => {
                                                            const plan = subscriptionPlans.find((p) => p.id === id);
                                                            return <Chip key={id} label={plan?.name || id} color="warning" />;
                                                        })}
                                                    </Box>
                                                )}
                                            >
                                                {subscriptionPlans.map((plan) => (
                                                    <MenuItem key={plan.id} value={plan.id}>
                                                        <Checkbox checked={coupon.applicablePlans.includes(plan.id)} />
                                                        <ListItemText primary={plan.name + " (" + plan.durationInDays + " Days) ₹" + plan.price} />
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                )}

                                <Box display="flex" justifyContent="space-between" >
                                    <Grid item xs={12}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    name="isActive"
                                                    checked={coupon.isActive}
                                                    onChange={handleChange}
                                                />
                                            }
                                            label="Active"
                                        />
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    name="oneTimePerUser"
                                                    checked={coupon.oneTimePerUser}
                                                    onChange={handleChange}
                                                />
                                            }
                                            label="One Time Per User"
                                        />
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    name="autoApply"
                                                    checked={coupon.autoApply}
                                                    onChange={handleChange}
                                                />
                                            }
                                            label="Auto Apply"
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <Button
                                            variant="contained"
                                            type="submit"
                                            size="large"
                                            fullWidth
                                            sx={{
                                                borderRadius: '20px',
                                                backgroundColor: '#1b1914',
                                                '&:hover': { backgroundColor: '#503e02ff' }
                                            }}
                                        >
                                            Submit
                                        </Button>
                                    </Grid>
                                </Box>
                            </Grid>
                        </Box>
                    </CardContent>
                </Card>
            </Box>
            {/* <CouponList coupons={coupons} /> */}
        </LocalizationProvider>
    );
};

export default CreateCouponForm;
